import React, { useMemo } from "react";
import { FiInbox } from "react-icons/fi";
import RowActionsMenu from "../RowActionsMenu/RowActionsMenu";

const formatRs = (n) =>
  `Rs ${Number(n || 0).toLocaleString("en-PK", { maximumFractionDigits: 0 })}`;

const SOURCE_LABELS = {
  customer_payment: {
    label: "Customer",
    className: "bg-successSoft text-success border-success/20",
  },
  guest_invoice: {
    label: "Guest",
    className: "bg-warningSoft text-warning border-warning/20",
  },
  company_payment: {
    label: "Company",
    className: "bg-subtle text-ink border-line",
  },
  expense: {
    label: "Expense",
    className: "bg-dangerSoft text-danger border-danger/20",
  },
};

const isEditablePayment = (row) =>
  row?.source === "customer_payment" || row?.source === "company_payment";

const CashTxnCard = ({ row, onEditPayment, onDeletePayment }) => {
  const meta = SOURCE_LABELS[row.source] || SOURCE_LABELS.customer_payment;
  const isIn = Number(row.dr) > 0;
  const canManage = isEditablePayment(row);

  return (
    <div className="rounded-xl border border-line bg-card p-4 shadow-soft">
      <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
        <span
          className={`text-xs font-bold px-2.5 py-1 rounded-full border ${meta.className}`}
        >
          {meta.label} {isIn ? "(In)" : "(Out)"}
        </span>
        <div className="flex items-center gap-1">
          <span className="text-xs font-semibold text-muted">{row.n_date}</span>
          {canManage && (
            <RowActionsMenu
              row={row}
              onEdit={onEditPayment}
              onDelete={onDeletePayment}
            />
          )}
        </div>
      </div>

      <p className="text-sm font-bold text-ink leading-snug">{row.desc}</p>
      {row.party && row.party !== "Expense" && (
        <p className="text-xs text-muted mt-1">{row.party}</p>
      )}
      {row.invoice && row.invoice !== "-" && (
        <p className="text-xs text-muted mt-0.5">Inv #{row.invoice}</p>
      )}

      <div className="flex flex-wrap items-end justify-between gap-3 mt-3 pt-3 border-t border-line">
        <div>
          {Number(row.dr) > 0 && (
            <p className="text-sm font-extrabold text-success tabular-nums">
              + {formatRs(row.dr)}
            </p>
          )}
          {Number(row.cr) > 0 && (
            <p className="text-sm font-extrabold text-danger tabular-nums">
              − {formatRs(row.cr)}
            </p>
          )}
        </div>
        <div className="text-right">
          <p className="text-[10px] uppercase tracking-wide text-muted font-semibold">
            Balance
          </p>
          <p className="text-sm font-bold text-main tabular-nums">
            {formatRs(row.bal)}
          </p>
        </div>
      </div>
    </div>
  );
};

const CashTxnCardList = ({
  entries,
  searchText,
  emptyMessage,
  onEditPayment,
  onDeletePayment,
}) => {
  const filtered = useMemo(() => {
    const q = searchText.trim().toLowerCase();
    if (!q) return entries || [];
    return (entries || []).filter(
      (row) =>
        String(row.desc || "").toLowerCase().includes(q) ||
        String(row.party || "").toLowerCase().includes(q) ||
        String(row.invoice || "").toLowerCase().includes(q) ||
        String(row.n_date || "").includes(q)
    );
  }, [entries, searchText]);

  if (!filtered.length) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4 rounded-2xl border-2 border-dashed border-line bg-subtle/80">
        <div className="w-16 h-16 rounded-2xl bg-main/10 flex items-center justify-center mb-4">
          <FiInbox className="text-main" size={28} />
        </div>
        <p className="font-bold text-inkSecondary text-lg">No transactions</p>
        <p className="text-sm text-muted mt-1 text-center max-w-xs">
          {searchText
            ? "Try a different search term."
            : emptyMessage || "No cash movements in this date range."}
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3 pb-4">
      {filtered.map((row) => (
        <CashTxnCard
          key={`${row.source}-${row._id}`}
          row={row}
          onEditPayment={onEditPayment}
          onDeletePayment={onDeletePayment}
        />
      ))}
    </div>
  );
};

export default CashTxnCardList;
